import { useEffect, useState } from 'react';
import type { MutableRefObject, ReactNode, RefObject } from 'react';

/**
 * Shared workroom mechanics (redesign port — the Actions-page system, shared so the laws stay
 * identical on every peer-item surface): slide-and-lock scroll, the scroll-spy reading line,
 * and the masthead / index / slide shells.
 *
 * Adapted from the prototype for this SPA's layout: the document body never scrolls here —
 * surfaces scroll inside `.v2-content` — so both hooks take the scroll CONTAINER ref instead
 * of window/documentElement.
 */

export type ItemRefMap = MutableRefObject<Map<string, HTMLDivElement | null>>;

/** Slide-and-lock: the scroll container snaps while the page is mounted, cleared on unmount
 *  so every other surface scrolls normally. */
export function useSlideScroll(containerRef: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.scrollSnapType = 'y mandatory';
    return () => {
      el.style.scrollSnapType = '';
    };
  }, [containerRef]);
}

/** Scroll-spy: tracks the workspace under the reading line (~140px from the viewport top) —
 *  last item whose top passed the line, else the first. `jump` scrolls to an item and marks
 *  it visible immediately. */
export function useScrollSpy(
  itemRefs: ItemRefMap,
  initialId: string,
  deps: unknown[],
  containerRef: RefObject<HTMLElement>,
) {
  const [visibleId, setVisibleId] = useState(initialId);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const line = 140;
      let bestId: string | null = null;
      let bestTop = -Infinity;
      let firstId: string | null = null;
      let firstTop = Infinity;
      itemRefs.current.forEach((el, id) => {
        if (!el || !el.isConnected) return;
        const top = el.getBoundingClientRect().top;
        if (top < firstTop) { firstTop = top; firstId = id; }
        if (top <= line && top > bestTop) { bestTop = top; bestId = id; }
      });
      const next = bestId ?? firstId;
      if (next) setVisibleId((current) => (current === next ? current : next));
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    container.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => container.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  const jump = (id: string) => {
    setVisibleId(id);
    itemRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return { visibleId, jump };
}

/** Page masthead: eyebrow · serif title · intro, two stat tiles at right.
 *  Doubles as the top slide on snapping pages (inert elsewhere). */
export function PageMast({ eyebrow, title, intro, tiles }: { eyebrow: string; title: ReactNode; intro: string; tiles: ReactNode }) {
  return (
    <section className="snap-start scroll-mt-20 grid gap-5 py-7 lg:grid-cols-[1.2fr_.8fr]">
      <div>
        <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[.2em] text-muted-foreground">{eyebrow}</p>
        <h1 className="mt-2 font-['Instrument_Serif'] text-[clamp(38px,4.4vw,60px)] font-normal leading-[.95] tracking-[-.02em]">{title}</h1>
        <p className="mt-4 max-w-xl text-[15px] leading-6 text-muted-foreground">{intro}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 self-end">{tiles}</div>
    </section>
  );
}

export function MastTile({ label, value, note }: { label: string; value: ReactNode; note: string }) {
  return (
    <div className="rounded-[20px] border border-border bg-card p-4 shadow-[0_12px_25px_rgba(88,69,42,.05)]">
      <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">{label}</span>
      <p className="mt-4 font-['Instrument_Serif'] text-4xl leading-8">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{note}</p>
    </div>
  );
}

/** Two-column workroom shell: boxed sticky index left, scroll-through workspaces right. */
export function WorkroomGrid({ index, children }: { index: ReactNode; children: ReactNode }) {
  return (
    <section className="grid items-start gap-5 lg:grid-cols-[.58fr_1.42fr]">
      <section className="rounded-[24px] border border-border bg-card p-4 shadow-[0_16px_34px_rgba(88,69,42,.055)] sm:p-5 lg:sticky lg:top-4">{index}</section>
      <div>{children}</div>
    </section>
  );
}

/** Index section: label turns accent while the reader is inside it. */
export function IndexGroup({ label, active, children }: { label: string; active?: boolean; children: ReactNode }) {
  return (
    <div className="mt-5 first:mt-0">
      <p className={`px-1 font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] transition-colors ${active ? 'text-acc' : 'text-muted-foreground'}`}>{label}</p>
      <div className="mt-2 space-y-1">{children}</div>
    </div>
  );
}

/** Main-column section label above a run of slides. */
export function SlideSectionLabel({ children }: { children: ReactNode }) {
  return <p className="mb-3 font-['DM_Mono'] text-[10px] uppercase tracking-[.18em] text-muted-foreground">{children}</p>;
}

/** One workspace slide: registers its ref for scroll-spy and locks 48px below the container
 *  top (the section label stays visible above it). */
export function Slide({ id, itemRefs, children }: { id: string; itemRefs: ItemRefMap; children: ReactNode }) {
  return <div ref={(el) => { itemRefs.current.set(id, el); }} className="snap-start scroll-mt-12">{children}</div>;
}
